import * as RT from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { useEffect, useState } from "react";

export type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  delayMs?: number;
  disabled?: boolean;
  className?: string;
};

export function TooltipProvider({ children, delayMs = 200 }: { children: React.ReactNode; delayMs?: number }) {
  return <RT.Provider delayDuration={delayMs}>{children}</RT.Provider>;
}

export function Tooltip({ content, children, side = "top", align = "center", delayMs, disabled, className }: TooltipProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <RT.Root
      delayDuration={delayMs}
      open={disabled ? false : open}
      onOpenChange={(nextOpen) => setOpen(disabled ? false : nextOpen)}
    >
      <RT.Trigger asChild>{children}</RT.Trigger>
      {!disabled && <RT.Portal>
        <RT.Content
          side={side}
          align={align}
          sideOffset={6}
          className={clsx(
            // Canonical tooltip — sharp 2px, no arrow; bg = the frame (surface-sunken: black
            // in dark, matching topbar/sidebar). Border keeps it defined against that frame.
            "z-50 max-w-xs px-2 py-1 text-xs rounded-[2px] shadow-[var(--shadow-md)]",
            "border border-[var(--color-border-default)] bg-[var(--surface-sunken)] text-[var(--color-text-primary)]",
            "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
            className,
          )}
        >
          {content}
        </RT.Content>
      </RT.Portal>}
    </RT.Root>
  );
}
